import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, Upload, X } from "lucide-react";
import TabsCst from "@components/TabsCst";
import UploadModalCst from "@components/UploadModalCst";
import { openToast } from "@utils/toastStore";
import UsersTab from "../../Enrollment/steps/tabs/UsersTab";
import GroupsTab from "../../Enrollment/steps/tabs/GroupsTab";
import { SelectedTargetItem } from "../../Enrollment/types";
import { useEditionEnrollmentSubmit } from "./hooks/useEditionEnrollmentSubmit";
import { useEditionCsvEnrollment } from "./hooks/useEditionCsvEnrollment";
import { EditionEnrollmentTriggerPayload } from "./types";

interface EditionEnrollmentModalProps {
  trigger: EditionEnrollmentTriggerPayload;
  onClose: () => void;
}

type Tab = "users" | "groups";

const EditionEnrollmentModal: React.FC<EditionEnrollmentModalProps> = ({
  trigger,
  onClose,
}) => {
  const { t } = useTranslation("edition-enrollment");
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState<Tab>("users");
  const [users, setUsers] = useState<SelectedTargetItem[]>([]);
  const [groups, setGroups] = useState<SelectedTargetItem[]>([]);
  const [csvOpen, setCsvOpen] = useState(false);

  const { submit, isSubmitting } = useEditionEnrollmentSubmit();
  const { uploadCsv, downloadTemplate } = useEditionCsvEnrollment({
    editionId: trigger.editionId,
    corporateId: trigger.corporateId,
    objectId: trigger.objectId,
    kind: activeTab,
  });

  const totalSelected = users.length + groups.length;

  const tabs = [
    { key: "users", label: String(t("tabs.users")) },
    { key: "groups", label: String(t("tabs.groups")) },
  ];

  const handleConfirm = async () => {
    try {
      await submit({ trigger, users, groups });
      trigger.onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: String(t("feedback.success")),
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message: e?.message || String(t("feedback.error")),
      });
    }
  };

  return (
    <>
      <Dialog
        open
        onClose={isSubmitting ? undefined : onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ padding: "20px 24px 0" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.text.tertiary,
                }}
              >
                <GraduationCap size={16} />
                <Typography sx={{ fontSize: "13px" }}>
                  {trigger.objectTitle || "-"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mt: 0.5,
                }}
              >
                {t("title")}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              aria-label="close"
              disabled={isSubmitting}
              sx={{ color: theme.palette.text.primary }}
            >
              <X size={20} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <TabsCst
              tabs={tabs}
              activeKey={activeTab}
              onChange={(k) => setActiveTab(k as Tab)}
            />
            <Button
              variant="text"
              startIcon={<Upload size={14} />}
              onClick={() => setCsvOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              {t("upload-csv")}
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "28px 24px 24px",
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          }}
        >
          {activeTab === "users" && (
            <UsersTab
              corporateId={trigger.corporateId}
              selected={users}
              onChange={setUsers}
            />
          )}
          {activeTab === "groups" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Alert severity="info" sx={{ borderRadius: "10px" }}>
                {t("groups-limit-banner")}
              </Alert>
              <GroupsTab
                corporateId={trigger.corporateId}
                selected={groups}
                onChange={setGroups}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "12px 24px 20px" }}>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isSubmitting || totalSelected === 0}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
            }}
          >
            {isSubmitting ? String(t("submitting")) : String(t("confirm"))}
          </Button>
        </DialogActions>
      </Dialog>

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(t("csv.upload-title"))}
        accept=".csv,text/csv"
        banner={{
          title: String(t("csv.banner.title")),
          text: String(
            t(
              activeTab === "groups"
                ? "csv.banner.text-groups"
                : "csv.banner.text",
            ),
          ),
          downloadLabel: String(t("csv.banner.download")),
          onDownload: downloadTemplate,
        }}
        onUpload={uploadCsv}
        onUploaded={() => trigger.onSuccess?.()}
      />
    </>
  );
};

export default EditionEnrollmentModal;
